// 韌體釋出 API layer(V4P-1;firmware-server 00 v0.2)— typed wrappers over
// the restful admin proxy /v3/admin/fw/*(瀏覽器不可達 CN 內部面,restful 白
// 名單代理;回應 envelope 與 firmware-server 同形直通)。時間戳 µs。
import { apiFetch } from '../../lib/api';

export interface FWImage {
  model: string;
  version: string;
  hw_version?: string;
  owner_scope?: string;
  size?: number;
  signature?: string;
  min_supported_version?: string;
  created_at?: number;
}

export interface FWRelease {
  model: string;
  release_id: string;
  version: string;
  hw_version: string;
  channel: string;
  base?: string;
  state: string; // draft|active|paused|superseded|rolled_back…
  review_state?: string;
  review_notes?: string;
  owner_scope?: string;
  prev_release_id?: string;
  stats?: { succeeded?: number; failed?: number };
  activated_at?: number;
  created_at?: number;
  // ADR-050 staged-stable rollout authority(firmware authority release DTO,
  // 經 restful /v3/admin/fw/* 白名單代理直通;JSON 名與 firmware-server domain
  // Release 一致)。時間戳同 created_at:µs epoch(用 formatTimestampUs)。
  stage?: string;              // lab|internal|canary|early|broad|complete
  stage_eligible_at?: number;  // µs epoch;此刻前不得推進(server 權威)
  pause_reason?: string;       // auto-pause(健康守門)原因;paused 時填
  state_revision?: number;     // CAS 版次;推進/動作以此樂觀鎖(衝突→409 CONFLICT)
  health_passed?: boolean;     // 最近一次健康評估是否通過
  last_health?: { evaluation_status?: string }; // 健康評估(firmware authority 巢狀吐、admin 代理不扁平化):healthy|unhealthy|insufficient_data
  lab_evidence_ready?: boolean;      // lab stage 實測證據是否就緒(推進 lab→internal 前置)
}

export interface FWTargeting {
  scope: string;
  model: string;
  kind: string;
  devices?: string[];
}

export async function listImages(model: string): Promise<FWImage[]> {
  // firmware-server 的 images 查詢必帶 model(internal 契約)
  const res = await apiFetch<FWImage[]>('/v3/admin/fw/images', { query: { model } });
  return (res.data as FWImage[] | null) ?? [];
}

/** firmware-server model-index 的一列:有 release/image 的機型(firmware-server#5)。 */
export interface FWModelIndex {
  model: string;
  has_image?: boolean;
  has_release?: boolean;
}

/** model 候選 chips:firmware-server model-index —— **所有有 release/image 的機型**
 *  (firmware-server#5;取代舊 `/v3/admin/devices?limit=100` 裝置抽樣,涵蓋有韌體
 *  但無在線裝置、或排在抽樣外的機型)。 */
export async function listKnownModels(): Promise<string[]> {
  const res = await apiFetch<FWModelIndex[]>('/v3/admin/fw/models');
  const rows = (res.data as FWModelIndex[] | null) ?? [];
  return rows.map((r) => r.model).filter(Boolean).sort();
}

export async function listReleases(model: string): Promise<FWRelease[]> {
  const res = await apiFetch<FWRelease[]>('/v3/admin/fw/releases', { query: { model } });
  return (res.data as FWRelease[] | null) ?? [];
}

export async function listTargeting(model: string): Promise<FWTargeting[]> {
  const res = await apiFetch<FWTargeting[]>('/v3/admin/fw/targeting', { query: { model } });
  return (res.data as FWTargeting[] | null) ?? [];
}

// 'advance':推進 staged-stable rollout 到下一 stage(lab→internal→canary→…→
// complete);後端狀態機 + CAS(state_revision)權威,健康守門未過/未到 eligible
// 時間時回 409 CONFLICT。activate/pause/resume/rollback 為既有生命週期動作。
export type FWAction = 'activate' | 'pause' | 'resume' | 'rollback' | 'advance';

/** 生命週期動作的操作理由/案件編號:pause(kill switch)與 rollback(不可逆)
 *  後端必填(缺→400),activate/resume 選填;皆併入 audit_logs(#73,與 RTBF /
 *  team revoke-role / company void / customer-family 一致)。 */
export interface ReleaseActionInput {
  reason?: string;
  case_id?: string;
}

export async function releaseAction(
  model: string,
  releaseID: string,
  action: FWAction,
  input: ReleaseActionInput = {},
): Promise<FWRelease> {
  const res = await apiFetch<FWRelease>(
    `/v3/admin/fw/releases/${encodeURIComponent(model)}/${encodeURIComponent(releaseID)}/${action}`,
    { method: 'POST', body: { reason: input.reason ?? '', case_id: input.case_id ?? '' } }
  );
  return res.data as FWRelease;
}
